// Mobile app shell: sticky header, independently scrolling content, fixed
// bottom tab bar. The shell itself is exactly 100dvh (never bare 100vh, the
// mobile browser chrome makes that jump) and never scrolls, only .shell-main
// does. Safe area insets are applied to the header top and the tab bar
// bottom so nothing sits under a notch or a home indicator.
//
// Round 5 tab bar: Beranda, Peta, Timeline, Cari, Profil. Capture is not a
// tab any more, it is reached from the map's floating control and from the
// Beranda shortcut, both one tap.
import type { ReactNode } from "react";
import { Icon, IconArrowLeft, IconFeed, IconHome, IconMap, IconTelescope, IconUser } from "@traveldiary/ui";
import { hrefFor, type TabName } from "../lib/router";
import { useApp } from "../state/app";
import { SyncBadge } from "./SyncBadge";
import type { TranslationKey } from "../lib/i18n";

const TABS: Array<{ name: TabName; labelKey: TranslationKey; glyph: ReactNode }> = [
  { name: "beranda", labelKey: "nav.home", glyph: <IconHome /> },
  { name: "map", labelKey: "nav.map", glyph: <IconMap /> },
  // A post card with an image block: the tab is a feed of other travellers'
  // diary entries. The old glyph was three lines plus a double headed arrow,
  // which is a SORT control, not a feed.
  { name: "timeline", labelKey: "nav.timeline", glyph: <IconFeed /> },
  // Deliberately NOT a compass. A compass reads as "navigate", which in a map
  // centred product collides with the Peta tab two slots away. This tab is a
  // deck for finding somewhere new to go, so the glyph is about looking for
  // a destination, not about orientation.
  { name: "cari", labelKey: "nav.discover", glyph: <IconTelescope /> },
  { name: "profil", labelKey: "nav.profile", glyph: <IconUser /> },
];

export interface ShellProps {
  /** Which tab stays lit. Destination screens light their parent tab. */
  active: TabName;
  title: string;
  actions?: ReactNode;
  /**
   * Full bleed screens (the map, the viewfinder, the deck, the search grid)
   * opt out of content padding AND out of the shell's scroll, because they
   * manage their own. Anything inside such a screen that can overflow has to
   * own its own scroll container: see .capture-caption in styles.css.
   */
  bleed?: boolean;
  /** Shown on destination screens, absent on the five tabs themselves. */
  onBack?: () => void;
  /** The login screen keeps the shell chrome but has nowhere to navigate yet. */
  hideTabs?: boolean;
  children: ReactNode;
}

export function Shell({ active, title, actions, bleed, onBack, hideTabs, children }: ShellProps) {
  const { t } = useApp();

  return (
    <div className="shell">
      <header className="shell-header">
        <div className="shell-header-row">
          {onBack ? (
            <button type="button" className="btn-icon" onClick={onBack} aria-label={t("common.back")}>
              <Icon size={20}>
                <IconArrowLeft />
              </Icon>
            </button>
          ) : null}
          <h1 className="shell-title">{title}</h1>
          <div className="shell-actions">
            {actions}
            <SyncBadge />
          </div>
        </div>
      </header>

      <main className={bleed ? "shell-main shell-main-bleed" : "shell-main"}>{children}</main>

      <nav
        className="tabbar"
        aria-label={t("app.title")}
        hidden={hideTabs || undefined}
        style={hideTabs ? { display: "none" } : undefined}
      >
        {TABS.map((tab) => {
          const isActive = tab.name === active;
          return (
            <a
              key={tab.name}
              className={isActive ? "tab tab-active" : "tab"}
              href={hrefFor(tab.name)}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon size={22}>{tab.glyph}</Icon>
              <span className="tab-label">{t(tab.labelKey)}</span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}
